// src/components/operationCenter/widgets/GenericChartWidget.tsx
// Generic line chart widget — fetches any backend endpoint and renders a line chart.

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Settings } from "lucide-react";
import type { EChartsOption } from "echarts";
import type { WidgetProps, GenericChartConfig } from "@/types/dashboard";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import {
  fetchEndpoint,
  extractRecords,
  parseParams,
  paramsToString,
} from "./shared/genericWidgetUtils";
import type { AnyRecord } from "./shared/genericWidgetUtils";
import {
  CHART_COLORS,
  createTooltip,
  createGrid,
  createXAxis,
  createYAxis,
  getSeriesColor,
} from "@/utils/charts/theme";
import { WIDGET_EMPTY } from "../WidgetWrapper";

function buildOption(
  records: AnyRecord[],
  xField: string,
  yFields: string[],
  yAxisFormat: GenericChartConfig["y_axis_format"],
): EChartsOption {
  const firstX = records[0]?.[xField];
  const isNumericTs = typeof firstX === "number";
  const isDateStr =
    typeof firstX === "string" && !isNaN(Date.parse(firstX));
  const useTimeAxis = isNumericTs || isDateStr;

  // Normalise x values: seconds → ms for numeric timestamps
  const xValues = records.map((r) => {
    const v = r[xField];
    if (isNumericTs && typeof v === "number") {
      return v < 1e10 ? v * 1000 : v; // seconds → ms
    }
    return v as string | number;
  });

  const yFormatter =
    yAxisFormat === "currency"
      ? (v: number) => `$${v.toLocaleString()}`
      : yAxisFormat === "percent"
        ? (v: number) => `${(v * 100).toFixed(2)}%`
        : undefined;

  const series = yFields.map((field, i) => {
    const color = getSeriesColor(i);
    const data: unknown[] = useTimeAxis
      ? records.map((r, idx) => [xValues[idx], r[field] as number])
      : records.map((r) => r[field] as number);

    return {
      name: field,
      type: "line" as const,
      data,
      smooth: false,
      showSymbol: false,
      lineStyle: { width: 2, color },
      itemStyle: { color },
    };
  });

  return {
    backgroundColor: CHART_COLORS.background,
    tooltip: createTooltip(),
    grid: createGrid(),
    legend:
      yFields.length > 1
        ? { show: true, textStyle: { color: CHART_COLORS.text.secondary, fontSize: 11 } }
        : { show: false },
    xAxis: useTimeAxis
      ? {
          type: "time" as const,
          axisLabel: { color: CHART_COLORS.axis.label, fontSize: 11 },
          axisLine: { lineStyle: { color: CHART_COLORS.axis.line } },
          splitLine: { show: false },
        }
      : createXAxis({ data: xValues as string[], boundaryGap: false }),
    yAxis: createYAxis(yFormatter ? { formatter: yFormatter } : {}),
    series,
  } as EChartsOption;
}

const GenericChartWidget: React.FC<WidgetProps<GenericChartConfig>> = ({
  widget,
  onConfigChange,
}) => {
  const config = widget.config as GenericChartConfig;

  // Show config form if no endpoint is set yet
  const [showConfig, setShowConfig] = useState(!config.endpoint);

  // Local editable state for the config form
  const [draftEndpoint, setDraftEndpoint] = useState(config.endpoint ?? "");
  const [draftTitle, setDraftTitle] = useState(config.title ?? "Chart");
  const [draftXField, setDraftXField] = useState(config.x_field ?? "timestamp");
  const [draftYFields, setDraftYFields] = useState(
    (config.y_fields ?? ["value"]).join(", "),
  );
  const [draftYFormat, setDraftYFormat] = useState<GenericChartConfig["y_axis_format"]>(
    config.y_axis_format ?? "number",
  );
  const [draftParams, setDraftParams] = useState(
    paramsToString(config.params),
  );

  // Keep draft in sync if config changes from outside (e.g. template load)
  useEffect(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Chart");
    setDraftXField(config.x_field ?? "timestamp");
    setDraftYFields((config.y_fields ?? ["value"]).join(", "));
    setDraftYFormat(config.y_axis_format ?? "number");
    setDraftParams(paramsToString(config.params));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget.id]);

  // Measure chart container height
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartHeight, setChartHeight] = useState(250);

  useEffect(() => {
    const update = () => {
      if (containerRef.current) {
        const h = containerRef.current.clientHeight;
        if (h > 0) setChartHeight(h);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Data fetching
  const {
    data: rawData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["generic_chart", config.endpoint, config.params],
    queryFn: ({ signal }) =>
      fetchEndpoint(config.endpoint, config.params ?? {}, signal),
    enabled: Boolean(config.endpoint),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Parse data into chart option
  const { chartOption, hasData } = useMemo(() => {
    if (!rawData) return { chartOption: {} as EChartsOption, hasData: false };
    const records = extractRecords(rawData);
    if (!records.length) return { chartOption: {} as EChartsOption, hasData: false };
    const yFields = config.y_fields?.length ? config.y_fields : ["value"];
    return {
      chartOption: buildOption(records, config.x_field ?? "timestamp", yFields, config.y_axis_format ?? "number"),
      hasData: true,
    };
  }, [rawData, config.x_field, config.y_fields, config.y_axis_format]);

  // Save config form
  const handleSave = useCallback(() => {
    const yFields = draftYFields
      .split(",")
      .map((f) => f.trim())
      .filter(Boolean);
    const params = parseParams(draftParams);
    onConfigChange?.({
      endpoint: draftEndpoint,
      title: draftTitle,
      x_field: draftXField,
      y_fields: yFields.length ? yFields : ["value"],
      y_axis_format: draftYFormat,
      ...(Object.keys(params).length ? { params } : {}),
    });
    setShowConfig(false);
  }, [
    draftEndpoint,
    draftTitle,
    draftXField,
    draftYFields,
    draftYFormat,
    draftParams,
    onConfigChange,
  ]);

  const handleCancel = useCallback(() => {
    setDraftEndpoint(config.endpoint ?? "");
    setDraftTitle(config.title ?? "Chart");
    setDraftXField(config.x_field ?? "timestamp");
    setDraftYFields((config.y_fields ?? ["value"]).join(", "));
    setDraftYFormat(config.y_axis_format ?? "number");
    setDraftParams(paramsToString(config.params));
    setShowConfig(false);
  }, [config]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2 border-b border-slate-700/30">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-medium truncate">
          {config.title || "Generic Chart"}
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refetch()}
            disabled={!config.endpoint}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200"
            title="Refresh"
          >
            <RefreshCw size={12} />
          </button>
          <button
            onClick={() => setShowConfig((s) => !s)}
            className="btn btn-ghost btn-xs p-1 text-slate-400 hover:text-slate-200"
            title="Configure"
          >
            <Settings size={12} />
          </button>
        </div>
      </div>

      {/* Config form */}
      {showConfig && (
        <div className="flex-shrink-0 p-3 border-b border-slate-700/30 bg-gray-900/50 space-y-2 overflow-y-auto max-h-64">
          {/* Endpoint */}
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">
              Endpoint
            </label>
            <input
              type="text"
              value={draftEndpoint}
              onChange={(e) => setDraftEndpoint(e.target.value)}
              placeholder="/btc/price-history  or  https://..."
              className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
            />
          </div>

          {/* Title + X field */}
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">
                Title
              </label>
              <input
                type="text"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="Chart title"
                className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
              />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">
                X Field
              </label>
              <input
                type="text"
                value={draftXField}
                onChange={(e) => setDraftXField(e.target.value)}
                placeholder="timestamp"
                className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
              />
            </div>
          </div>

          {/* Y fields + format */}
          <div className="flex flex-wrap gap-2">
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">
                Y Fields (comma-sep)
              </label>
              <input
                type="text"
                value={draftYFields}
                onChange={(e) => setDraftYFields(e.target.value)}
                placeholder="value, price"
                className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
              />
            </div>
            <div className="min-w-[120px] flex-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wide">
                Y Format
              </label>
              <select
                value={draftYFormat}
                onChange={(e) =>
                  setDraftYFormat(
                    e.target.value as GenericChartConfig["y_axis_format"],
                  )
                }
                className="select select-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
              >
                <option value="number">Number</option>
                <option value="currency">Currency ($)</option>
                <option value="percent">Percent (%)</option>
              </select>
            </div>
          </div>

          {/* Query params */}
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wide">
              Query Params
            </label>
            <input
              type="text"
              value={draftParams}
              onChange={(e) => setDraftParams(e.target.value)}
              placeholder="token=BTC&limit=100"
              className="input input-xs w-full bg-gray-800/60 border-gray-700/50 text-slate-200 text-xs mt-0.5"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-1">
            <button
              onClick={handleCancel}
              className="btn btn-xs btn-ghost text-slate-400"
            >
              Cancel
            </button>
            <button onClick={handleSave} className="btn btn-xs btn-primary">
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Chart area */}
      <div ref={containerRef} className="flex-1 min-h-0">
        {!config.endpoint ? (
          <div className={WIDGET_EMPTY}>
            <div className="text-center space-y-2">
              <p>No endpoint configured</p>
              <button
                onClick={() => setShowConfig(true)}
                className="btn btn-xs btn-outline btn-primary"
              >
                Configure
              </button>
            </div>
          </div>
        ) : (
          <EChartsRenderer
            chartOption={chartOption}
            height={chartHeight}
            isLoading={isLoading}
            error={error instanceof Error ? error.message : (error ? String(error) : null)}
            hasData={hasData}
            containerClassName=""
          />
        )}
      </div>
    </div>
  );
};

export default GenericChartWidget;
